import type { UsageSnapshot } from '@detect/shared';
import { useTenantQuery } from '../../hooks/useApi';
import { LoadingSpinner, ErrorState, EmptyState } from '../../components/common';

interface UsageResponse {
  clientId: string | null;
  startDate: string;
  endDate: string;
  days: (UsageSnapshot & { id: string })[];
  truncated: boolean;
}

/** Tenant-level metering (PRD §2.12): the rebilling/billing input.
 *  Per-client breakdowns ride the same endpoint with ?clientId. */
export function UsageTab() {
  const query = useTenantQuery<UsageResponse>(['usage'], '/usage');

  if (query.isLoading) return <LoadingSpinner />;
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const usage = query.data;

  return (
    <div>
      <p className="text-muted">
        Daily tenant-level metering, {usage.startDate} → {usage.endDate}. This is
        the billing/rebilling input; snapshots are computed nightly.
      </p>
      {usage.days.length === 0 ? (
        <EmptyState
          title="No usage yet"
          description="Snapshots appear after the first nightly metering sweep."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Resources</th>
                <th>Devices</th>
                <th>Websites</th>
                <th>Cloud</th>
                <th>Collectors</th>
                <th>Datapoints</th>
              </tr>
            </thead>
            <tbody>
              {usage.days.map((d) => (
                <tr key={d.id}>
                  <td>{d.date}</td>
                  <td>{d.resourceCounts.total}</td>
                  <td>{d.resourceCounts.byKind.device}</td>
                  <td>{d.resourceCounts.byKind.website}</td>
                  <td>{d.resourceCounts.byKind.cloud}</td>
                  <td>{d.activeCollectors}</td>
                  <td>{d.datapointsIngested.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
